// CRM & Comms — pipeline, hot-leads banner, recent communications, and
// lead-card detail modal.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   page section:    line 2666 (#page-crm)
//   list renderer:   line 6953 (window.renderCrm)
//   data getter:     line 6918 (window._crmGetAll / KOS_CRM_SEED)
//   pipeline panels: line 6992
//   hot strip:       line 7013
//   recent comms:    line 7022
//   job profile modal (the one with the BUG):
//                    line 14677 (openJobProfile) — the line we are fixing is
//                    14705: `$('jp-address').textContent = data.address || '--';`
//                    No type-check, so a jsonb `{line1,…}` becomes the literal
//                    string "[object Object]". The master doc lists this as
//                    priority bug #1 and this rebuild fixes it via
//                    `formatAddress()` below.
//
// Backend:
//   /api/crm/contacts (mounted by /var/www/kitchenos-api/modules/crm.js;
//   exposed via server.js:2019). Endpoints:
//     GET    /api/crm/contacts                       (this page uses this)
//     POST   /api/crm/contacts                       (upsert, not used here)
//     PUT    /api/crm/contacts/:id                   (patch, not used here)
//     DELETE /api/crm/contacts/:id                   (not used here)
//   Note: in the legacy schema the `address` field is stored either as a
//   string OR as a jsonb object {line1, line2, city, county, postcode}. The
//   sanitiser in modules/crm.js coerces it back to a string on write, but
//   downstream/legacy data may still arrive as an object — `formatAddress`
//   below handles both cases.
//
//   There are no dedicated /api/portal/communications or /api/portal/leads/:id
//   routes for the retailer portal at extraction time. We render recent
//   communications from realistic demo data when those endpoints 404.

import { ApiError } from "@tkos/api-client";
import { confirmDialog, maskPhone as maskPhoneShared, type MiniCrmCustomer } from "@tkos/shared-ui";
import type { CommChannel, LeadStage } from "@tkos/tkos-types";

import { registerPage } from "../../shell/router";
import { customerCache } from "../../shell/customer-cache";
import { openCustomerComposeModal } from "../../shell/customer-compose-modal";
import { openNewCustomerModal } from "../../shell/new-customer-modal";
import { demoDataAllowed } from "../../shell/onboarding";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

/** Address as it can appear from the backend: jsonb object OR plain string. */
type AddressLike =
  | string
  | null
  | undefined
  | {
      line1?: string;
      line2?: string;
      city?: string;
      county?: string;
      postcode?: string;
      // tolerate unknown keys without erroring
      [k: string]: unknown;
    };

/** What we get from /api/crm/contacts (loose — sanitiser already applied server-side). */
interface ServerContact {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  stage?: string;
  /** Title-case "Lead" / "Qualified" / etc — legacy. We coerce. */
  notes?: string;
  /** Free-form jsonb-or-string. */
  address?: AddressLike;
  street?: string;
  town?: string;
  postcode?: string;
  county?: string;
  kitchenType?: string;
  budget?: string;
  value?: number;
  /** Real total of the most-recently issued quote, when one exists. Set by the
   *  /crm/contacts endpoint so a quoted card shows the quote value, not budget. */
  quoteTotal?: number;
  project?: string;
  nextAction?: string;
  lastTouch?: string;
  /** Unix-ms last-touch timestamp; preferred over freeform `lastTouch` for relative-time. */
  lastTouchAt?: number | string;
  score?: number;
  hot?: boolean;
  designsCount?: number;
}

/** Normalised in-memory shape used by the page. */
interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  stage: LeadStage;
  /** Pre-formatted, user-safe address string (NEVER `[object Object]`). */
  address: string;
  project: string;
  value: number;
  nextAction: string;
  lastTouch: string;
  lastTouchAt: number | null;
  score: number;
  hot: boolean;
  designsCount: number;
}

interface CommItem {
  id: string;
  customer: string;
  channel: CommChannel;
  preview: string;
  /** ISO timestamp. */
  at: string;
}

// ── Lead-stage handling ──────────────────────────────────────────────────────

const STAGE_ORDER: readonly LeadStage[] = [
  "lead",
  "qualified",
  "quoted",
  "won",
  "lost",
];

interface StageSpec {
  key: LeadStage;
  label: string;
  color: string;
  bg: string;
}

const STAGES: readonly StageSpec[] = [
  { key: "lead", label: "Lead", color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  { key: "qualified", label: "Qualified", color: "#a855f7", bg: "rgba(168,85,247,0.12)" },
  { key: "quoted", label: "Quoted", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { key: "won", label: "Won", color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
  { key: "lost", label: "Lost", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
];

const STAGE_BY_KEY: Record<LeadStage, StageSpec> = STAGES.reduce(
  (acc, s) => {
    acc[s.key] = s;
    return acc;
  },
  {} as Record<LeadStage, StageSpec>,
);

const OPEN_STAGES: ReadonlySet<LeadStage> = new Set(["lead", "qualified", "quoted"]);

function coerceStage(raw: unknown): LeadStage {
  if (typeof raw !== "string") return "lead";
  const lc = raw.trim().toLowerCase();
  if (STAGE_ORDER.includes(lc as LeadStage)) return lc as LeadStage;
  // Tolerate legacy capitalisations / synonyms.
  if (lc === "new lead") return "lead";
  if (lc === "complete" || lc === "installed" || lc === "delivered") return "won";
  return "lead";
}

// ── Channel handling ─────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<CommChannel, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  phone: "Phone",
  sms: "SMS",
  in_app: "In-app",
};

const CHANNEL_COLORS: Record<CommChannel, string> = {
  email: "#3b82f6",
  whatsapp: "#22c55e",
  phone: "#a855f7",
  sms: "#0ea5e9",
  in_app: "#e58200",
};

function coerceChannel(raw: unknown): CommChannel {
  if (typeof raw !== "string") return "email";
  const lc = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (lc === "email" || lc === "whatsapp" || lc === "phone" || lc === "sms" || lc === "in_app") {
    return lc;
  }
  if (lc === "inapp") return "in_app";
  return "email";
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function relTime(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  if (abs < 86400 * 365) return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
  return RTF.format(Math.round(diffSec / (86400 * 365)), "year");
}

/** Mask phone for customer records (defraud-sacred — uses shared helper).
 *  See [[feedback-contact-masking]] for the canonical format. */
function maskPhone(phone: string): string {
  return maskPhoneShared(phone);
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** CSS.escape polyfill — string -> escaped attribute-selector value. */
function cssEsc(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => "\\" + c);
}

// Active mount's focus-handler — set by `mount`, cleared on cleanup.
// Exposed via `crmFocusCustomer` so the router/shell can focus a lead
// while CRM is already mounted.
let activeFocus: ((idOrName: string) => boolean) | null = null;

/** Programmatic API: focus the matching lead on the CRM page. Returns
 *  `true` if the page is mounted AND a lead was matched, else `false`.
 *  Useful when navigation happens while CRM is already on-screen. */
export function crmFocusCustomer(idOrName: string): boolean {
  return activeFocus?.(idOrName) ?? false;
}

// ── ★ ADDRESS RENDERING ─ priority bug #1 fix ────────────────────────────────
//
// Master-doc bug: lead-card modal renders address as `[object Object]` because
// the legacy code does `data.address || '--'` with no type check, and the
// backend's `address` column is jsonb shaped {line1, line2, city, county,
// postcode}. This helper handles every shape we expect to see.
//
// Inputs handled:
//   - `null` / `undefined`            → "—"
//   - `""`                            → "—"
//   - `"42 Elm Road, HG1 1AA"`        → "42 Elm Road, HG1 1AA"  (legacy strings)
//   - `{ line1, line2, city, … }`     → "line1, line2, city, county, postcode"
//                                       (skipping empty / non-string parts)
//   - `{ line1: "  42 Elm Road  " }`  → "42 Elm Road"  (trimmed)
//   - any other primitive / array     → "—"  (defensive — never crash, never
//                                              render `[object Object]`)
export function formatAddress(addr: unknown): string {
  if (addr == null) return "—";
  if (typeof addr === "string") {
    const t = addr.trim();
    // Legacy data-poisoning: earlier broken code wrote `String({…})` →
    // "[object Object]" into the address column. ~90 such records exist in
    // crm_contacts.json; the original address bytes are gone. Treat the
    // sentinel as "no address" so the lead card displays "—".
    if (t === "" || t === "[object Object]") return "—";
    return t;
  }
  if (typeof addr !== "object") return "—";
  if (Array.isArray(addr)) return "—";
  const a = addr as Record<string, unknown>;
  const parts = (["line1", "line2", "city", "county", "postcode"] as const)
    .map((k) => a[k])
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((v) => v.trim());
  return parts.length > 0 ? parts.join(", ") : "—";
}

// ── Normalisation ────────────────────────────────────────────────────────────

/**
 * Flatten the legacy `street/town/postcode/county` triple into an AddressLike
 * jsonb-shaped object so the same `formatAddress` formatter handles it.
 */
function buildAddressFromParts(c: ServerContact): AddressLike {
  if (c.address != null && (typeof c.address !== "string" || c.address.trim())) {
    return c.address;
  }
  const obj: { line1?: string; city?: string; county?: string; postcode?: string } = {};
  if (typeof c.street === "string" && c.street.trim()) obj.line1 = c.street.trim();
  if (typeof c.town === "string" && c.town.trim()) obj.city = c.town.trim();
  if (typeof c.county === "string" && c.county.trim()) obj.county = c.county.trim();
  if (typeof c.postcode === "string" && c.postcode.trim()) obj.postcode = c.postcode.trim();
  return obj;
}

function coerceTouchedAt(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return null;
}

/** Adapt a normalised CRM Lead into the mini-CRM popup's customer shape so
 *  the shell's mini-CRM cache can resolve clicks anywhere in the app. */
function leadToMiniCrm(l: Lead): MiniCrmCustomer {
  const m: MiniCrmCustomer = {
    id:    l.id,
    name:  l.name,
    hot:   l.hot,
    stage: STAGE_BY_KEY[l.stage].label,
    project: l.project,
    value: l.value,
    phone: l.phone,
    email: l.email,
    nextAction: l.nextAction,
    lastTouch: l.lastTouch,
    score: l.score,
    focusId: l.id,
  };
  // Best-effort parse of the formatted address: pull the last token as
  // the postcode if it looks like one (UK style "AB1 2CD").
  const addr = l.address;
  if (addr && addr !== "—") {
    const tokens = addr.split(",").map((t) => t.trim()).filter(Boolean);
    if (tokens.length >= 1) {
      const last = tokens[tokens.length - 1] ?? "";
      if (/^[A-Z]{1,2}\d[\dA-Z]?\s?\d[A-Z]{2}$/i.test(last)) {
        m.postcode = last;
        if (tokens.length >= 2) {
          const townTok = tokens[tokens.length - 2];
          if (townTok) m.town = townTok;
        }
        if (tokens.length >= 3) {
          const streetTok = tokens[0];
          if (streetTok) m.street = streetTok;
        }
      } else {
        // No postcode detected — keep the whole thing as the street.
        const streetTok = tokens[0];
        if (streetTok) m.street = streetTok;
        if (tokens.length >= 2) {
          const townTok = tokens[1];
          if (townTok) m.town = townTok;
        }
      }
    }
  }
  return m;
}

function normaliseContact(c: ServerContact): Lead {
  const id = String(c.id ?? `tmp-${Math.random().toString(36).slice(2)}`);
  const name = String(c.name ?? "").trim() || "Unnamed lead";
  const stage = coerceStage(c.stage);
  const address = formatAddress(buildAddressFromParts(c));
  // Prefer the real quote total once a quote has been issued for this contact;
  // otherwise fall back to the stored lead value, then the estimated budget band.
  const quoteTotal =
    typeof c.quoteTotal === "number" && Number.isFinite(c.quoteTotal) && c.quoteTotal > 0
      ? c.quoteTotal
      : 0;
  const value =
    quoteTotal > 0
      ? quoteTotal
      : typeof c.value === "number" && Number.isFinite(c.value)
        ? c.value
        : Number(String(c.budget ?? "0").replace(/[^0-9.]/g, "")) || 0;
  const score =
    typeof c.score === "number" && Number.isFinite(c.score)
      ? Math.max(0, Math.min(100, Math.round(c.score)))
      : 50;

  return {
    id,
    name,
    phone: String(c.phone ?? "").trim(),
    email: String(c.email ?? "").trim(),
    stage,
    address,
    project: String(c.project ?? c.kitchenType ?? "New enquiry").trim() || "New enquiry",
    value,
    nextAction: String(c.nextAction ?? c.notes ?? "—").trim() || "—",
    lastTouch: String(c.lastTouch ?? "").trim() || "—",
    lastTouchAt: coerceTouchedAt(c.lastTouchAt),
    score,
    hot: Boolean(c.hot),
    designsCount: typeof c.designsCount === "number" ? c.designsCount : 0,
  };
}

// ── Demo fallback (onboarding-gated) ─────────────────────────────────────────
//
// Demo/showcase data must NEVER render for a real retailer account. These
// helpers therefore return the empty/zero state unless the retailer has
// explicitly completed onboarding (`demoDataAllowed`). The showcase rows were
// stripped 2026-06-05, so even an onboarded account currently gets [].

function demoLeads(ctx: PageContext): Lead[] {
  if (!demoDataAllowed(ctx)) return [];
  // Empty/zero state — the pipeline renders empty until live CRM contacts load.
  return [];
}

function demoComms(ctx: PageContext): CommItem[] {
  if (!demoDataAllowed(ctx)) return [];
  // Empty/zero state — the recent-communications list renders empty until a
  // live /portal/communications feed lands.
  return [];
}

// ── Backend ──────────────────────────────────────────────────────────────────

interface CrmContactsResponse {
  ok?: boolean;
  contacts?: ServerContact[];
}

interface CommsResponse {
  ok?: boolean;
  communications?: Array<{
    id?: string;
    customer?: string;
    channel?: string;
    preview?: string;
    at?: string;
  }>;
}

async function loadLeads(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ leads: Lead[]; usedFallback: boolean }> {
  try {
    const resp = await ctx.api<CrmContactsResponse>("/crm/contacts", { signal });
    const list = Array.isArray(resp?.contacts) ? resp.contacts : [];
    if (list.length === 0) {
      // Empty store on this retailer — show demo so the page is useful.
      return { leads: demoLeads(ctx), usedFallback: true };
    }
    return { leads: list.map(normaliseContact), usedFallback: false };
  } catch (err) {
    if (signal.aborted || isAbortError(err)) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return { leads: demoLeads(ctx), usedFallback: true };
    }
    throw err;
  }
}

async function loadComms(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<{ comms: CommItem[]; usedFallback: boolean }> {
  try {
    const resp = await ctx.api<CommsResponse>("/portal/communications?limit=10", { signal });
    const list = Array.isArray(resp?.communications) ? resp.communications : [];
    if (list.length === 0) return { comms: demoComms(ctx), usedFallback: true };
    return {
      comms: list.map(
        (c, i): CommItem => ({
          id: String(c.id ?? `c-${i}`),
          customer: String(c.customer ?? "Unknown").trim(),
          channel: coerceChannel(c.channel),
          preview: String(c.preview ?? "").trim(),
          at: String(c.at ?? new Date().toISOString()),
        }),
      ),
      usedFallback: false,
    };
  } catch (err) {
    if (signal.aborted || isAbortError(err)) throw err;
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return { comms: demoComms(ctx), usedFallback: true };
    }
    // Any other failure — fall back rather than blank the section.
    console.warn("[crm] comms fetch failed; using demo:", err);
    return { comms: demoComms(ctx), usedFallback: true };
  }
}

// ── Stage aggregation ────────────────────────────────────────────────────────

interface StageSummary extends StageSpec {
  count: number;
  total: number;
  pct: number;
}

interface SummaryStats {
  pipelineValue: number;
  pipelineCount: number;
  wonValue: number;
  wonCount: number;
  winRatePct: number;
  decidedCount: number;
  activeCustomers: number;
  hotLeads: number;
}

function summarise(leads: Lead[]): { stages: StageSummary[]; stats: SummaryStats } {
  const total = leads.reduce((s, l) => s + (l.value || 0), 0);
  const stages = STAGES.map<StageSummary>((s) => {
    const items = leads.filter((l) => l.stage === s.key);
    const stageTotal = items.reduce((a, l) => a + (l.value || 0), 0);
    const pct = total > 0 ? Math.round((stageTotal / total) * 100) : 0;
    return { ...s, count: items.length, total: stageTotal, pct };
  });

  const pipelineLeads = leads.filter((l) => OPEN_STAGES.has(l.stage));
  const wonLeads = leads.filter((l) => l.stage === "won");
  // "decided" = won + lost (anything no longer in the pipeline).
  const decided = leads.filter((l) => l.stage === "won" || l.stage === "lost");
  const winRatePct = decided.length > 0 ? Math.round((wonLeads.length / decided.length) * 100) : 0;

  const stats: SummaryStats = {
    pipelineValue: pipelineLeads.reduce((s, l) => s + l.value, 0),
    pipelineCount: pipelineLeads.length,
    wonValue: wonLeads.reduce((s, l) => s + l.value, 0),
    wonCount: wonLeads.length,
    winRatePct,
    decidedCount: decided.length,
    activeCustomers: pipelineLeads.length,
    hotLeads: leads.filter((l) => l.hot && OPEN_STAGES.has(l.stage)).length,
  };

  return { stages, stats };
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderShell(container: HTMLElement): {
  statsEl: HTMLElement;
  attentionEl: HTMLElement;
  pipelineEl: HTMLElement;
  commsEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="crm-root" aria-label="CRM &amp; Comms">
      <header class="crm-header">
        <div class="crm-header__titles">
          <h1 class="crm-title">CRM &amp; Comms</h1>
          <p class="crm-subtitle">Leads, customers, projects and follow-ups in one place</p>
        </div>
      </header>
      <div class="crm-stats" data-role="stats" role="list"></div>
      <div class="crm-attention" data-role="attention" hidden></div>
      <section class="crm-pipeline" aria-labelledby="crm-pipeline-title">
        <div class="crm-pipeline__header">
          <span id="crm-pipeline-title">Pipeline</span>
          <span class="crm-pipeline__hint">Drag a card to move stage</span>
        </div>
        <div class="crm-pipeline__cols" data-role="pipeline" role="list"></div>
      </section>
      <section class="crm-comms" aria-labelledby="crm-comms-title">
        <div class="crm-comms__header">
          <span id="crm-comms-title">Recent communications</span>
          <span class="crm-comms__hint">All channels</span>
        </div>
        <ul class="crm-comms__list" data-role="comms"></ul>
      </section>
    </section>
  `;

  return {
    statsEl: container.querySelector<HTMLElement>("[data-role='stats']")!,
    attentionEl: container.querySelector<HTMLElement>("[data-role='attention']")!,
    pipelineEl: container.querySelector<HTMLElement>("[data-role='pipeline']")!,
    commsEl: container.querySelector<HTMLElement>("[data-role='comms']")!,
  };
}

function renderStats(host: HTMLElement, stats: SummaryStats): void {
  const cards: Array<{ key: string; label: string; value: string; sub: string; color: string; bg: string }> = [
    {
      key: "pipeline",
      label: "Pipeline Value",
      value: GBP.format(stats.pipelineValue),
      sub: `${stats.pipelineCount} open opportunit${stats.pipelineCount === 1 ? "y" : "ies"}`,
      color: "#e58200",
      bg: "rgba(245,158,11,0.12)",
    },
    {
      key: "won",
      label: "Won All Time",
      value: GBP.format(stats.wonValue),
      sub: `${stats.wonCount} deal${stats.wonCount === 1 ? "" : "s"} closed`,
      color: "#22c55e",
      bg: "rgba(34,197,94,0.12)",
    },
    {
      key: "winRate",
      label: "Win Rate",
      value: `${stats.winRatePct}%`,
      sub: `vs ${stats.decidedCount} decided deal${stats.decidedCount === 1 ? "" : "s"}`,
      color: "#a855f7",
      bg: "rgba(168,85,247,0.12)",
    },
    {
      key: "active",
      label: "Active Customers",
      value: String(stats.activeCustomers),
      sub: "across all open stages",
      color: "#3b82f6",
      bg: "rgba(59,130,246,0.12)",
    },
  ];

  host.innerHTML = cards
    .map(
      (c) => `
        <article class="crm-stat" role="listitem"
          style="--crm-stat-color:${c.color};--crm-stat-bg:${c.bg}">
          <div class="crm-stat__label">${escapeHtml(c.label)}</div>
          <div class="crm-stat__value">${escapeHtml(c.value)}</div>
          <div class="crm-stat__sub">${escapeHtml(c.sub)}</div>
        </article>
      `,
    )
    .join("");
}

function renderAttention(host: HTMLElement, leads: Lead[], stats: SummaryStats): void {
  if (stats.hotLeads === 0) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  const hotList = leads
    .filter((l) => l.hot && OPEN_STAGES.has(l.stage))
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 6);

  host.hidden = false;
  host.innerHTML = `
    <div class="crm-attention__title">
      <span class="crm-attention__bolt" aria-hidden="true">⚡</span>
      Needs attention today
      <span class="crm-attention__count">${stats.hotLeads} hot lead${stats.hotLeads === 1 ? "" : "s"}</span>
    </div>
    <ul class="crm-attention__list">
      ${hotList
        .map(
          (l) => `
            <li class="crm-attention__row">
              <button type="button" class="crm-attention__btn"
                data-lead-id="${escapeHtml(l.id)}"
                data-customer-name="${escapeHtml(l.name)}"
                data-customer-id="${escapeHtml(l.id)}">
                <span class="crm-attention__name">${escapeHtml(l.name)}</span>
                <span class="crm-attention__action">${escapeHtml(l.nextAction)}</span>
                <span class="crm-attention__value">${escapeHtml(GBP.format(l.value))} · ${escapeHtml(STAGE_BY_KEY[l.stage].label)}</span>
              </button>
            </li>
          `,
        )
        .join("")}
    </ul>
  `;
}

function renderPipeline(host: HTMLElement, leads: Lead[], stages: StageSummary[], onAdd?: () => void): void {
  // Global empty state (no customers at all) — icon + message + clear action.
  if (leads.length === 0) {
    host.innerHTML = `
      <div class="crm-pipeline-empty">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:.5">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11h-6M19 8v6"/></svg>
        <p class="crm-pipeline-empty__h">No customers yet</p>
        <p class="crm-pipeline-empty__s">Add your first customer to start tracking your pipeline.</p>
        <button type="button" class="crm-btn crm-empty-add">+ Add customer</button>
      </div>`;
    if (onAdd) host.querySelector<HTMLButtonElement>(".crm-empty-add")?.addEventListener("click", onAdd);
    return;
  }
  host.innerHTML = stages
    .map((s) => {
      const items = leads
        .filter((l) => l.stage === s.key)
        .sort((a, b) => (b.score || 0) - (a.score || 0));
      const cards =
        items.length === 0
          ? `<li class="crm-stage__empty">No leads in this stage.</li>`
          : items
              .map(
                (l) => `
                  <li class="crm-stage__lead" data-lead-row="${escapeHtml(l.id)}">
                    <button type="button" class="crm-lead-card"
                      data-lead-id="${escapeHtml(l.id)}"
                      data-customer-name="${escapeHtml(l.name)}"
                      data-customer-id="${escapeHtml(l.id)}"
                      aria-label="Open lead card for ${escapeHtml(l.name)}">
                      <span class="crm-lead-card__avatar" aria-hidden="true"
                        style="background:${s.color}">${escapeHtml(initialsOf(l.name))}</span>
                      <span class="crm-lead-card__main">
                        <span class="crm-lead-card__name">
                          ${escapeHtml(l.name)}
                          ${l.hot ? `<span class="crm-lead-card__hot" title="Hot lead">HOT</span>` : ""}
                        </span>
                        <span class="crm-lead-card__project">${escapeHtml(l.project)}</span>
                        <span class="crm-lead-card__meta">
                          <span class="crm-lead-card__value">${escapeHtml(GBP.format(l.value))}</span>
                          <span class="crm-lead-card__score" title="Lead score">Score ${l.score}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                `,
              )
              .join("");
      return `
        <article class="crm-stage" role="listitem"
          style="--crm-stage-color:${s.color};--crm-stage-bg:${s.bg}"
          data-stage="${s.key}">
          <header class="crm-stage__header">
            <span class="crm-stage__label">${escapeHtml(s.label.toUpperCase())}</span>
            <span class="crm-stage__pct">${s.pct}%</span>
          </header>
          <div class="crm-stage__counts">
            <span class="crm-stage__count">${s.count}</span>
            <span class="crm-stage__total">${escapeHtml(GBP.format(s.total))}</span>
          </div>
          <div class="crm-stage__bar" aria-hidden="true">
            <span class="crm-stage__bar-fill" style="width:${s.pct}%"></span>
          </div>
          <ul class="crm-stage__leads">${cards}</ul>
        </article>
      `;
    })
    .join("");
}

function renderComms(host: HTMLElement, comms: CommItem[]): void {
  if (comms.length === 0) {
    host.innerHTML = `<li class="crm-comms__empty">No communications yet.</li>`;
    return;
  }
  host.innerHTML = comms
    .slice(0, 10)
    .map((c) => {
      const color = CHANNEL_COLORS[c.channel];
      const label = CHANNEL_LABELS[c.channel];
      const at = Date.parse(c.at);
      const when = Number.isFinite(at) ? relTime(at) : "—";
      return `
        <li class="crm-comm" data-customer-name="${escapeHtml(c.customer)}">
          <span class="crm-comm__avatar" aria-hidden="true"
            style="background:${color}22;color:${color}">${escapeHtml(initialsOf(c.customer))}</span>
          <div class="crm-comm__body">
            <div class="crm-comm__row">
              <span class="crm-comm__name">${escapeHtml(c.customer)}</span>
              <span class="crm-comm__badge" style="--crm-comm-color:${color}">${escapeHtml(label)}</span>
              <span class="crm-comm__when">${escapeHtml(when)}</span>
            </div>
            <div class="crm-comm__preview">${escapeHtml(c.preview)}</div>
          </div>
        </li>
      `;
    })
    .join("");
}

// ── Modal ────────────────────────────────────────────────────────────────────

interface ModalRefs {
  dialog: HTMLDialogElement;
  bodyEl: HTMLElement;
  titleEl: HTMLElement;
  closeBtn: HTMLButtonElement;
}

function buildModal(): ModalRefs {
  const dialog = document.createElement("dialog");
  dialog.className = "crm-modal";
  dialog.innerHTML = `
    <form method="dialog" class="crm-modal__form">
      <header class="crm-modal__head">
        <h2 class="crm-modal__title" data-role="title">Lead</h2>
        <button class="crm-modal__close" type="button" data-role="close" aria-label="Close">&times;</button>
      </header>
      <div class="crm-modal__body" data-role="body"></div>
      <footer class="crm-modal__footer">
        <button type="button" class="crm-btn" data-role="goto-crm">Go to CRM page</button>
        <button type="submit" class="crm-btn crm-btn--primary">Done</button>
      </footer>
    </form>
  `;
  return {
    dialog,
    bodyEl: dialog.querySelector<HTMLElement>("[data-role='body']")!,
    titleEl: dialog.querySelector<HTMLElement>("[data-role='title']")!,
    closeBtn: dialog.querySelector<HTMLButtonElement>("[data-role='close']")!,
  };
}

function fillModal(refs: ModalRefs, lead: Lead): void {
  const stage = STAGE_BY_KEY[lead.stage];
  refs.titleEl.textContent = lead.name;
  refs.bodyEl.innerHTML = `
    <div class="crm-modal__top">
      <span class="crm-modal__avatar" aria-hidden="true" style="background:${stage.color}">
        ${escapeHtml(initialsOf(lead.name))}
      </span>
      <div class="crm-modal__top-meta">
        <div class="crm-modal__name-row">
          <span class="crm-modal__name">${escapeHtml(lead.name)}</span>
          <span class="crm-modal__stage" style="background:${stage.bg};color:${stage.color}">
            ${escapeHtml(stage.label.toUpperCase())}
          </span>
          ${lead.hot ? `<span class="crm-modal__hot">HOT</span>` : ""}
        </div>
        <div class="crm-modal__project">${escapeHtml(lead.project)}</div>
      </div>
      <div class="crm-modal__value">${escapeHtml(GBP.format(lead.value))}</div>
    </div>

    <dl class="crm-modal__grid">
      <div>
        <dt>Phone</dt>
        <dd>${escapeHtml(maskPhone(lead.phone))}</dd>
      </div>
      <div>
        <dt>Address</dt>
        <dd data-role="address">${escapeHtml(lead.address)}</dd>
      </div>
      <div>
        <dt>Next action</dt>
        <dd>${escapeHtml(lead.nextAction)}</dd>
      </div>
      <div>
        <dt>Last touch</dt>
        <dd>${escapeHtml(lead.lastTouchAt != null ? relTime(lead.lastTouchAt) : lead.lastTouch)}</dd>
      </div>
    </dl>

    <div class="crm-modal__score">
      <div class="crm-modal__score-row">
        <span class="crm-modal__score-label">Lead score</span>
        <span class="crm-modal__score-val">${lead.score}/100</span>
      </div>
      <div class="crm-modal__score-bar" role="progressbar"
        aria-valuemin="0" aria-valuemax="100" aria-valuenow="${lead.score}"
        aria-label="Lead score">
        <span class="crm-modal__score-fill" style="width:${lead.score}%"></span>
      </div>
    </div>

    <div class="crm-modal__designs">
      <div>
        <span class="crm-modal__designs-count">${lead.designsCount}</span>
        <span class="crm-modal__designs-label">design${lead.designsCount === 1 ? "" : "s"} &amp; plan${lead.designsCount === 1 ? "" : "s"}</span>
      </div>
      <button type="button" class="crm-btn crm-btn--ghost" data-action="upload-design">Upload</button>
    </div>

    <div class="crm-modal__actions">
      <button type="button" class="crm-btn" data-action="call" data-lead-id="${escapeHtml(lead.id)}">
        Call
      </button>
      <button type="button" class="crm-btn" data-action="message" data-lead-id="${escapeHtml(lead.id)}">
        Message
      </button>
      <button type="button" class="crm-btn crm-btn--primary" data-action="full-record" data-lead-id="${escapeHtml(lead.id)}">
        Full record
      </button>
    </div>
  `;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const crmPage: Page = {
  name: "crm",
  label: "CRM & Comms",
  icon: "👥",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let leads: Lead[] = [];
    // The lead whose modal is currently open — so the modal's "Message" action
    // can open the inline compose popup against the right customer (Item 4).
    let activeLead: Lead | null = null;
    let comms: CommItem[] = [];
    let disposed = false;

    const { statsEl, attentionEl, pipelineEl, commsEl } = renderShell(container);

    // Modal — appended to root so backdrop covers everything inside the page.
    const modal = buildModal();
    container.appendChild(modal.dialog);

    function rerender(): void {
      const { stages, stats } = summarise(leads);
      renderStats(statsEl, stats);
      renderAttention(attentionEl, leads, stats);
      renderPipeline(pipelineEl, leads, stages, () => openNewCustomerModal({ showToast: ctx.showToast }));
      renderComms(commsEl, comms);
    }

    function openLead(id: string): void {
      const lead = leads.find((l) => l.id === id);
      if (!lead) return;
      activeLead = lead;
      fillModal(modal, lead);
      if (typeof modal.dialog.showModal === "function") {
        modal.dialog.showModal();
      } else {
        modal.dialog.setAttribute("open", "");
      }
    }

    function closeModal(): void {
      if (modal.dialog.open) modal.dialog.close();
    }

    // ── Stage move (drag-drop end-of-flow) ───────────────────────────────
    // Optimistic move, optional notify prompt for Quoted/Won, PUT to the
    // CRM endpoint, rollback on failure. Notify message goes via
    // POST /api/portal/customer-message (platform-only — Twilio/Resend are
    // notifications-only per the platform messaging rule).
    async function moveLeadToStage(lead: Lead, newStage: LeadStage): Promise<void> {
      const prevStage = lead.stage;
      if (newStage === prevStage) return;
      lead.stage = newStage;
      rerender();

      let shouldNotify = false;
      if (newStage === "quoted") {
        shouldNotify = await confirmDialog(
          `Would you like to notify ${lead.name} that their quote is ready?`,
          {
            title: "Notify customer?",
            confirmLabel: "Yes, send notification",
            cancelLabel: "No, just move",
          },
        );
      } else if (newStage === "won") {
        shouldNotify = await confirmDialog(
          `Would you like to notify ${lead.name} that their kitchen is confirmed?`,
          {
            title: "Notify customer?",
            confirmLabel: "Yes, send notification",
            cancelLabel: "No, just move",
          },
        );
      }

      try {
        await ctx.api(`/crm/contacts/${encodeURIComponent(lead.id)}`, {
          method: "PUT",
          body: JSON.stringify({ stage: newStage }),
        });
        ctx.track({ eventType: "action", feature: "crm", customData: { action: "stage-change", stage: newStage } });
      } catch (err) {
        lead.stage = prevStage;
        rerender();
        const msg = err instanceof ApiError
          ? `Couldn't update stage (HTTP ${err.status})`
          : "Couldn't update stage";
        ctx.showToast(msg, "error");
        return;
      }

      if (shouldNotify) {
        const firstName = lead.name.split(/\s+/)[0] || lead.name;
        const subject = newStage === "quoted"
          ? "Your kitchen quote is ready"
          : "Your kitchen is confirmed";
        const body = newStage === "quoted"
          ? `Hi ${firstName}, your kitchen quote is ready to view in your portal.`
          : `Hi ${firstName}, your kitchen is confirmed. We'll be in touch with next steps.`;
        try {
          await ctx.api("/portal/customer-message", {
            method: "POST",
            body: JSON.stringify({
              retailerId: ctx.user?.retailerId,
              retailerName: ctx.user?.retailerName,
              customerId: lead.id,
              customerName: lead.name,
              customerEmail: lead.email,
              subject,
              body,
            }),
          });
          ctx.showToast(`${firstName} notified`, "success");
        } catch (err) {
          const msg = err instanceof ApiError
            ? `Stage moved — notification failed (HTTP ${err.status})`
            : "Stage moved — notification failed";
          ctx.showToast(msg, "error");
        }
      }
    }

    // ── Drag-and-drop (pointer events: mouse + touch) ────────────────────
    // Movement >= DRAG_THRESHOLD enters drag mode; below that we treat the
    // gesture as a click and let the existing click handler open the modal.
    // `setPointerCapture` keeps pointermove flowing even when the pointer
    // leaves the original card.
    const DRAG_THRESHOLD = 6;
    interface DragState {
      pointerId: number;
      startX: number;
      startY: number;
      leadId: string;
      origin: HTMLElement;
      ghost: HTMLElement | null;
      active: boolean;
      ghostOffsetX: number;
      ghostOffsetY: number;
    }
    let drag: DragState | null = null;
    let suppressNextClick = false;

    function clearDropTargets(): void {
      pipelineEl
        .querySelectorAll<HTMLElement>(".crm-stage--drop-target")
        .forEach((el) => el.classList.remove("crm-stage--drop-target"));
    }

    function endDrag(): void {
      if (!drag) return;
      if (drag.ghost) drag.ghost.remove();
      drag.origin.classList.remove("crm-lead-card--dragging");
      document.body.classList.remove("crm-dragging");
      clearDropTargets();
      drag = null;
    }

    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== undefined && e.button !== 0) return;
      const card = (e.target as HTMLElement | null)?.closest<HTMLElement>(".crm-lead-card");
      if (!card) return;
      const leadId = card.getAttribute("data-lead-id");
      if (!leadId) return;
      const rect = card.getBoundingClientRect();
      drag = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        leadId,
        origin: card,
        ghost: null,
        active: false,
        ghostOffsetX: e.clientX - rect.left,
        ghostOffsetY: e.clientY - rect.top,
      };
      try {
        card.setPointerCapture(e.pointerId);
      } catch {
        /* iOS Safari can throw if the element is detached — non-fatal */
      }
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      if (!drag.active) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        drag.active = true;
        const rect = drag.origin.getBoundingClientRect();
        const ghost = drag.origin.cloneNode(true) as HTMLElement;
        ghost.classList.add("crm-lead-card--ghost");
        ghost.style.position = "fixed";
        ghost.style.left = `${rect.left}px`;
        ghost.style.top = `${rect.top}px`;
        ghost.style.width = `${rect.width}px`;
        ghost.style.pointerEvents = "none";
        ghost.style.zIndex = "9999";
        document.body.appendChild(ghost);
        drag.ghost = ghost;
        drag.origin.classList.add("crm-lead-card--dragging");
        document.body.classList.add("crm-dragging");
      }

      if (drag.ghost) {
        drag.ghost.style.left = `${e.clientX - drag.ghostOffsetX}px`;
        drag.ghost.style.top = `${e.clientY - drag.ghostOffsetY}px`;
      }

      // Hide the ghost briefly to query the element below, then restore.
      const ghostPrev = drag.ghost?.style.display ?? "";
      if (drag.ghost) drag.ghost.style.display = "none";
      const elBelow = document.elementFromPoint(e.clientX, e.clientY);
      if (drag.ghost) drag.ghost.style.display = ghostPrev;

      clearDropTargets();
      const stage = elBelow?.closest<HTMLElement>(".crm-stage");
      if (stage && pipelineEl.contains(stage)) {
        stage.classList.add("crm-stage--drop-target");
      }

      // Prevent the page from scrolling while a touch-drag is active.
      if (e.pointerType === "touch") {
        e.preventDefault();
      }
    };

    const onPointerUp = (e: PointerEvent): void => {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const wasActive = drag.active;
      const leadId = drag.leadId;

      let target: HTMLElement | null = null;
      if (wasActive) {
        const ghostPrev = drag.ghost?.style.display ?? "";
        if (drag.ghost) drag.ghost.style.display = "none";
        const elBelow = document.elementFromPoint(e.clientX, e.clientY);
        if (drag.ghost) drag.ghost.style.display = ghostPrev;
        target = elBelow?.closest<HTMLElement>(".crm-stage") ?? null;
      }

      endDrag();

      if (wasActive) {
        suppressNextClick = true;
        const stageKey = target?.getAttribute("data-stage");
        const validStage =
          stageKey && STAGE_ORDER.includes(stageKey as LeadStage)
            ? (stageKey as LeadStage)
            : null;
        if (validStage) {
          const lead = leads.find((l) => l.id === leadId);
          if (lead && lead.stage !== validStage) {
            void moveLeadToStage(lead, validStage);
          }
        }
      }
    };

    const onPointerCancel = (e: PointerEvent): void => {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const wasActive = drag.active;
      endDrag();
      if (wasActive) suppressNextClick = true;
    };

    pipelineEl.addEventListener("pointerdown", onPointerDown);
    pipelineEl.addEventListener("pointermove", onPointerMove);
    pipelineEl.addEventListener("pointerup", onPointerUp);
    pipelineEl.addEventListener("pointercancel", onPointerCancel);

    // ── Event delegation ─────────────────────────────────────────────────
    const onContainerClick = (e: MouseEvent): void => {
      if (suppressNextClick) {
        suppressNextClick = false;
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const leadBtn = target.closest<HTMLButtonElement>("[data-lead-id]");
      if (leadBtn) {
        const id = leadBtn.dataset.leadId;
        if (id) openLead(id);
      }
    };
    container.addEventListener("click", onContainerClick, true);

    const onModalClick = (e: MouseEvent): void => {
      // Backdrop click — close.
      if (e.target === modal.dialog) {
        closeModal();
        return;
      }
      const action = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-action]");
      if (!action) return;
      const a = action.dataset.action;
      if (a === "call") {
        ctx.showToast("Click-to-call coming soon", "info");
      } else if (a === "message") {
        // Inline compose popup (Item 4) — opens the existing conversation for
        // this customer if one exists, else starts a new one. The retailer
        // stays on the CRM page rather than navigating to Messages.
        if (activeLead) {
          const ownRetailerId =
            (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
            ctx.user?.retailerId ?? "";
          openCustomerComposeModal(
            {
              customer: { id: activeLead.id, name: activeLead.name, email: activeLead.email },
              ownRetailerId,
              retailerName: ctx.user?.retailerName ?? "Retailer",
            },
            ctx,
          );
        }
        closeModal();
      } else if (a === "full-record" || action.dataset.role === "goto-crm") {
        // Master-doc "Go to CRM page" — already on it; close modal.
        closeModal();
      } else if (a === "upload-design") {
        ctx.navigate("designs");
        closeModal();
      }
    };
    modal.dialog.addEventListener("click", onModalClick);

    const onModalRoleClick = (e: MouseEvent): void => {
      const role = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-role='goto-crm']");
      if (role) {
        // We are the CRM page — just close.
        closeModal();
      }
    };
    modal.dialog.addEventListener("click", onModalRoleClick);

    const onCloseClick = (): void => closeModal();
    modal.closeBtn.addEventListener("click", onCloseClick);

    // ── Focus support ─────────────────────────────────────────────────────
    // Scrolls the matching lead's row into view, adds an `is-focused` class
    // for a brief pulse, and pops the lead-detail modal for that customer.
    // Matches by id first, then by case-insensitive name.
    function focusLead(idOrName: string): boolean {
      const key = String(idOrName ?? "").trim();
      if (!key) return false;
      const lc = key.toLowerCase();
      const lead =
        leads.find((l) => l.id === key) ??
        leads.find((l) => l.name.toLowerCase() === lc);
      if (!lead) return false;

      const row = container.querySelector<HTMLElement>(
        `[data-lead-row="${cssEsc(lead.id)}"]`,
      );
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        row.classList.add("is-focused");
        // Strip the class once the animation finishes so re-focuses retrigger.
        window.setTimeout(() => row.classList.remove("is-focused"), 1800);
      }
      openLead(lead.id);
      return true;
    }
    activeFocus = focusLead;

    // ── Data fan-out ─────────────────────────────────────────────────────
    async function load(): Promise<void> {
      // First load only — skeleton the pipeline while data arrives (polls keep
      // their existing content so nothing flashes).
      if (!leads.length) {
        pipelineEl.innerHTML = `<div class="skeleton skeleton--row" style="height:54px"></div>`
          + `<div class="skeleton skeleton--row" style="height:54px;width:88%"></div>`
          + `<div class="skeleton skeleton--row" style="height:54px;width:76%"></div>`;
      }
      try {
        const [leadsRes, commsRes] = await Promise.all([
          loadLeads(ctx, ac.signal),
          loadComms(ctx, ac.signal),
        ]);
        if (disposed || ac.signal.aborted) return;
        leads = leadsRes.leads;
        comms = commsRes.comms;
        if (leadsRes.usedFallback || commsRes.usedFallback) {
          // Demo fallback is silent on-screen — diagnostic only.
          console.warn("[crm] live data unavailable — using demo figures");
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load CRM (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Couldn't load CRM";
        leads = demoLeads(ctx);
        comms = demoComms(ctx);
        ctx.showToast(msg, "error");
      } finally {
        if (disposed) return;
        // Publish into the cross-page mini-CRM cache so clicks elsewhere
        // can resolve a full customer record.
        customerCache.merge(leads.map(leadToMiniCrm));
        rerender();
        // If the shell handed us a focus hint, honour it after first render.
        const focus = ctx.params["focus"];
        if (focus) focusLead(focus);
      }
    }

    // Initial paint with demo so the page never flashes empty…
    leads = [];
    comms = [];
    rerender();
    void load();

    // Refresh on customer-created broadcast from the shared add-customer modal.
    const onCustomerCreated = (): void => {
      void load();
    };
    window.addEventListener("tkos:customer-created", onCustomerCreated);

    // Refresh on customer-deleted (shell bridges the realtime `contact.deleted`
    // broadcast to this window event) so a deletion in another tab drops the
    // pipeline card here without a manual reload.
    const onCustomerDeleted = (): void => {
      void load();
    };
    window.addEventListener("tkos:customer-deleted", onCustomerDeleted);

    // ── Cleanup ──────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      if (activeFocus === focusLead) activeFocus = null;
      endDrag();
      pipelineEl.removeEventListener("pointerdown", onPointerDown);
      pipelineEl.removeEventListener("pointermove", onPointerMove);
      pipelineEl.removeEventListener("pointerup", onPointerUp);
      pipelineEl.removeEventListener("pointercancel", onPointerCancel);
      container.removeEventListener("click", onContainerClick, true);
      modal.dialog.removeEventListener("click", onModalClick);
      modal.dialog.removeEventListener("click", onModalRoleClick);
      modal.closeBtn.removeEventListener("click", onCloseClick);
      window.removeEventListener("tkos:customer-created", onCustomerCreated);
      window.removeEventListener("tkos:customer-deleted", onCustomerDeleted);
      if (modal.dialog.open) modal.dialog.close();
    };
  },
};

registerPage(crmPage);

export default crmPage;
